import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  BoxProps,
  Container,
  Stack,
  SvgIcon,
  Typography
} from '@mui/material'
import parse from 'html-react-parser'

import Button from '@/components/base/button'

import { WPLink } from '@/types'

export type AccordionGridProps = BoxProps &
  Partial<{
    items: {
      header: string
      content: string
      actions?: {
        read?: WPLink
        download?: WPLink
      }
    }[]
  }>

const AccordionGrid = ({ items, ...props }: AccordionGridProps) => {
  return (
    <Box {...props}>
      <Container>
        {items?.map(({ header, content, actions }, index) => {
          const needsActions = actions && (actions?.read?.url || actions?.download?.url)
          const hasContent = content && content.trim() !== ''

          return (
            <Accordion
              key={`item-${index}`}
              disableGutters
              elevation={0}
              disabled={!hasContent}
              sx={{
                '&.Mui-disabled': {
                  background: 'transparent!important',
                  color: 'inherit',
                  opacity: '1!important',
                  '& *': {
                    opacity: '1!important',
                    pointerEvents: 'all'
                  }
                }
              }}
            >
              <AccordionSummary
                id={`panel${index}-header`}
                aria-controls={`panel${index}-content`}
                expandIcon={
                  !needsActions && hasContent ? (
                    <SvgIcon fontSize="small">
                      <use href="#ico-arrow-down" />
                    </SvgIcon>
                  ) : null
                }
              >
                <Stack
                  component="span"
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={4}
                  width="100%"
                >
                  <Typography component="h2" variant="h4" color="text.secondaryContrastText">
                    {header}
                  </Typography>

                  {needsActions ? (
                    <Stack direction="row" spacing={1} marginRight={{ sm: '-3.2rem' }}>
                      {actions && actions?.read?.url ? (
                        <Button
                          component="span"
                          variant="opacity"
                          icon="plus"
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          {actions.read?.title}
                        </Button>
                      ) : null}

                      {actions && actions?.download?.url ? (
                        <Button
                          component="a"
                          variant="outlined"
                          href={actions.download.url}
                          icon="pdf"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {actions.download?.title}
                        </Button>
                      ) : null}
                    </Stack>
                  ) : null}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="textRegular" color="text.primary" component="p">
                  {parse(
                    `${content ?? ''}${actions && actions?.read?.url ? ` <a target="_blank" href="${actions.read.url}">${actions.read.title}</a>` : ''}`
                  )}
                </Typography>
              </AccordionDetails>
            </Accordion>
          )
        })}
      </Container>
    </Box>
  )
}

export default AccordionGrid
